import React, { useState, useEffect } from 'react';
import { FiBell, FiFilter, FiTrash2, FiCheckCircle } from 'react-icons/fi';
import notificationService from '../services/notificationService';
import './Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    is_read: '',
    type: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [filters]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService.getNotifications(filters);
      setNotifications(data.notifications.map(n => notificationService.formatNotification(n)));
      setPagination(data.pagination || {});
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await notificationService.getStats();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      fetchNotifications();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      fetchNotifications();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark all as read');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await notificationService.delete(id);
      fetchNotifications();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete notification');
    }
  };

  const handleDeleteAllRead = async () => {
    if (!window.confirm('Are you sure you want to delete all read notifications?')) {
      return;
    }

    try {
      await notificationService.deleteAllRead();
      fetchNotifications();
      fetchStats();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete notifications');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
  };

  const groupedNotifications = notificationService.groupByDate(notifications);

  if (loading && notifications.length === 0) {
    return <div className="loading">Loading notifications...</div>;
  }

  return (
    <div className="notifications-page">
      <div className="page-header">
        <div className="header-left">
          <h1><FiBell /> Notifications</h1>
          {stats && (
            <div className="notification-stats">
              <span className="stat-badge unread">{stats.unread} Unread</span>
              <span className="stat-badge total">{stats.total} Total</span>
            </div>
          )}
        </div>
        <div className="header-actions">
          {stats && stats.unread > 0 && (
            <button className="btn btn-primary" onClick={handleMarkAllAsRead}>
              <FiCheckCircle /> Mark All as Read
            </button>
          )}
          {stats && stats.read > 0 && (
            <button className="btn btn-danger" onClick={handleDeleteAllRead}>
              <FiTrash2 /> Delete All Read
            </button>
          )}
        </div>
      </div>

      <div className="notifications-filters">
        <div className="filter-group">
          <FiFilter />
          <select name="is_read" value={filters.is_read} onChange={handleFilterChange}>
            <option value="">All Notifications</option>
            <option value="false">Unread Only</option>
            <option value="true">Read Only</option>
          </select>
        </div>

        <div className="filter-group">
          <select name="type" value={filters.type} onChange={handleFilterChange}>
            <option value="">All Types</option>
            <option value="comment_added">Comments</option>
            <option value="mention">Mentions</option>
            <option value="task_assigned">Task Assigned</option>
            <option value="task_completed">Task Completed</option>
            <option value="project_status_changed">Project Updates</option>
            <option value="milestone_reached">Milestones</option>
            <option value="budget_exceeded">Budget Alerts</option>
            <option value="risk_identified">Risk Alerts</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="notifications-content">
        {Object.keys(groupedNotifications).length === 0 ? (
          <div className="empty-state">
            <FiBell size={64} />
            <h3>No notifications</h3>
            <p>You're all caught up!</p>
          </div>
        ) : (
          Object.entries(groupedNotifications).map(([dateGroup, notifs]) => (
            notifs.length > 0 && (
              <div key={dateGroup} className="notification-group">
                <h3 className="group-title">{dateGroup}</h3>
                <div className="notification-list">
                  {notifs.map(notification => (
                    <div
                      key={notification.id}
                      className={`notification-card ${!notification.is_read ? 'unread' : ''}`}
                    >
                      <div className="notification-icon" style={{ color: notification.color }}>
                        {notification.icon}
                      </div>
                      <div className="notification-body">
                        <div className="notification-header">
                          <h4>{notification.title}</h4>
                          <span className="notification-time">{notification.timeAgo}</span>
                        </div>
                        <p className="notification-message">{notification.message}</p>
                        <div className="notification-meta">
                          <span className="notification-type">{notification.typeLabel}</span>
                        </div>
                      </div>
                      <div className="notification-actions">
                        {!notification.is_read && (
                          <button
                            className="btn-icon"
                            onClick={() => handleMarkAsRead(notification.id)}
                            title="Mark as read"
                          >
                            <FiCheckCircle />
                          </button>
                        )}
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDelete(notification.id)}
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            disabled={filters.page === 1}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </button>
          <span>Page {filters.page} of {pagination.pages}</span>
          <button
            disabled={filters.page === pagination.pages}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Notifications;


